import * as React from 'react';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { SchoolEvent } from 'Src/models/event';
import { getKitLabKits, KitLabBackendKit } from 'Src/helpers/service/admin/kitLab';
import {
  isSkortPatternId,
  mapBottomToSkortPattern,
  resolveDetailColor,
  getLayoutForKit
} from 'Src/helpers/kitLab/kitLab';
import { getSchoolEventDetails, updateSchoolEventDetails } from 'Src/helpers/service/admin/schoolEvents';
import { KitShirtPreview } from 'Src/components/KitLab/KitShirtPreview';
import { KitShortsPreview } from 'Src/components/KitLab/KitShortsPreview';
import { KitSkortPreview } from 'Src/components/KitLab/KitSkortPreview';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { KitSelector } from 'Src/components/KitLab/KitSelector';
import { Button } from 'Src/components/Button/Button';
import { Loader } from 'Src/components/Loader/Loader';
import './SchoolEventKitSummary.scss';

interface Props {
  user: AppUser;
  event: SchoolEvent;
  eventsDetails: any;
}

export const SchoolEventKitSummary: React.FC<Props> = ({ user, event, eventsDetails }) => {
  const [details, setDetails] = React.useState<any>(eventsDetails);

  const [allKits, setAllKits] = React.useState<KitLabBackendKit[] | null>(null);
  const [isKitsLoading, setIsKitsLoading] = React.useState<boolean>(false);

  const [isKitModalOpen, setIsKitModalOpen] = React.useState(false);
  const [modalTeamId, setModalTeamId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDetails(eventsDetails);
  }, [eventsDetails]);

  React.useEffect(() => {
    const hasMappings = Array.isArray(details?.kitLabKits) && details.kitLabKits.length > 0;
    if (!hasMappings) return;

    setIsKitsLoading(true);
    getKitLabKits(user)
      .then(kits => {
        setAllKits(kits || []);
      })
      .catch(err => {
        console.error('Error loading Kit Lab kits for summary:', err);
      })
      .then(() => {
        setIsKitsLoading(false);
      });
  }, [user, details && details.kitLabKits && details.kitLabKits.length]);

  const findMappingForTeam = React.useCallback(
    (teamId: string | null) => {
      const list: any[] = propz.get(details, ['kitLabKits'], []) || [];
      const key = teamId ? teamId.toString() : null;

      let mapping =
        list.find(item => {
          const cur = item.teamId ? item.teamId.toString() : null;
          return cur === key;
        }) || null;

      if (!mapping) {
        mapping = list.find(item => !item.teamId) || null;
      }

      return mapping;
    },
    [details]
  );

  const findKitById = React.useCallback(
    (kitId?: string | null): KitLabBackendKit | null => {
      if (!kitId || !allKits) return null;
      const idStr = kitId.toString();
      return allKits.find(k => k._id && k._id.toString() === idStr) || null;
    },
    [allKits]
  );

  const ownTeams = React.useMemo(() => {
    const all = propz.get(event, ['embeddedTeams'], []);
    const activeSchoolId = user.activeSchoolId ? user.activeSchoolId.toString() : null;

    if (!Array.isArray(all) || !activeSchoolId) return [];
    return all.filter(t => {
      const sid = t.schoolId ? t.schoolId.toString() : null;
      return sid === activeSchoolId;
    });
  }, [event, user.activeSchoolId]);

  const openKitModalForTeam = (teamId: string | null) => {
    setModalTeamId(teamId);
    setIsKitModalOpen(true);
  };

  const handleSelectKitForTeam = async (teamId: string | null, kit: KitLabBackendKit) => {
    if (!event?.id || !kit?._id) {
      setIsKitModalOpen(false);
      return;
    }

    try {
      const current: any[] = propz.get(details, ['kitLabKits'], []) || [];
      const key = teamId ? teamId.toString() : null;

      const idx = current.findIndex(item => {
        const cur = item.teamId ? item.teamId.toString() : null;
        return cur === key;
      });

      const entry: any = {
        ...(idx !== -1 ? current[idx] : {}),
        kitId: kit._id,
        teamId: teamId || undefined
      };

      const next = [...current];
      if (idx === -1) {
        next.push(entry);
      } else {
        next[idx] = entry;
      }

      await updateSchoolEventDetails(user, event.id, {
        kitLabKits: next
      });

      const fresh = await getSchoolEventDetails(user, event.id);
      setDetails(fresh);
    } catch (e) {
      console.error('Failed to update kit for team/event', e);
    } finally {
      setIsKitModalOpen(false);
    }
  };

  const handleClearKitForTeam = async (teamId: string | null) => {
    if (!event?.id) {
      setIsKitModalOpen(false);
      return;
    }

    try {
      const current: any[] = propz.get(details, ['kitLabKits'], []) || [];
      const key = teamId ? teamId.toString() : null;

      const next = current.filter(item => {
        const cur = item.teamId ? item.teamId.toString() : null;
        return cur !== key;
      });

      await updateSchoolEventDetails(user, event.id, {
        kitLabKits: next
      });

      const fresh = await getSchoolEventDetails(user, event.id);
      setDetails(fresh);
    } catch (e) {
      console.error('Failed to clear kit for team/event', e);
    } finally {
      setIsKitModalOpen(false);
    }
  };

  const renderKitModal = () => {
    if (!isKitModalOpen) return null;

    const teamId = modalTeamId;
    const isEventLevel = !teamId;

    const embeddedTeams: any[] = propz.get(event, ['embeddedTeams'], []) || [];
    const teamObj = teamId ? embeddedTeams.find(t => t._id && t._id.toString() === teamId) : null;

    const fakeTeamForKitSelector: any = {
      id: teamId || event.id,
      sportId: event.sportId,
      gender: teamObj?.gender || event.gender
    };

    const mappingForEntity = findMappingForTeam(teamId);
    const currentKitId = mappingForEntity && mappingForEntity.kitId ? mappingForEntity.kitId.toString() : null;

    const currentKit = currentKitId && allKits ? allKits.find(k => k._id && k._id.toString() === currentKitId) : null;

    const title = isEventLevel
      ? 'Choose kit for this event'
      : teamObj?.name
      ? `Choose kit for team ${teamObj.name}`
      : 'Choose kit for this team';

    return (
      <SimpleModal
        isOpen={isKitModalOpen}
        onCloseClick={() => setIsKitModalOpen(false)}
        title={title}
        customClass="eKitSelectorModal eKitSelectorModal--compact"
        dialogStyle={{ maxWidth: 680 }}
        showFooter={false}
      >
        <div className="eKitSelectorModal__teamInfo">
          {!isEventLevel && teamObj && <div className="eKitSelectorModal__teamName">{teamObj.name}</div>}

          {currentKit ? (
            <div className="eKitSelectorModal__currentKit">
              Currently selected kit:
              <span className="eKitSelectorModal__currentKitName">{currentKit.name || 'Unnamed kit'}</span>
            </div>
          ) : (
            <div className="eKitSelectorModal__currentKit eKitSelectorModal__currentKit--none">
              No kit selected yet for this {isEventLevel ? 'event' : 'team'}.
            </div>
          )}
        </div>

        <KitSelector
          user={user}
          team={fakeTeamForKitSelector}
          onSelect={(kit: KitLabBackendKit) => handleSelectKitForTeam(teamId, kit)}
          selectedKitId={currentKitId || undefined}
          onClear={() => handleClearKitForTeam(teamId)}
          onCancel={() => setIsKitModalOpen(false)}
        />
      </SimpleModal>
    );
  };

  const renderSection = (teamId: string | null, title: string) => {
    const mapping = findMappingForTeam(teamId);
    const kitIdStr = mapping && mapping.kitId ? mapping.kitId.toString() : undefined;
    const kit = findKitById(kitIdStr);

    const key = teamId || 'event';
    const subjectText = teamId ? 'team' : 'event';

    if (!kitIdStr) {
      return (
        <div className="eEventKitSummaryRow eEventKitSummaryRow--empty" key={key}>
          <div className="eEventKitSummaryRow__preview eEventKitSummaryRow__preview--empty">
            <span>Kit</span>
          </div>
          <div className="eEventKitSummaryRow__info">
            <div className="eEventKitSummaryRow__title">{title}</div>
            <div className="eEventKitSummaryRow__emptyText">No kit selected yet for this {subjectText}.</div>
          </div>
          <div className="eEventKitSummaryRow__actions">
            <Button
              text="Choose kit"
              onClick={() => openKitModalForTeam(teamId)}
              customClass="sit-btn sit-btn--primary sit-btn--sm"
            />
          </div>
        </div>
      );
    }

    if (kitIdStr && !kit) {
      if (isKitsLoading) {
        return (
          <div className="eEventKitSummaryRow" key={key}>
            <div className="eEventKitSummaryRow__preview eEventKitSummaryRow__preview--empty">
              <Loader />
            </div>
            <div className="eEventKitSummaryRow__info">
              <div className="eEventKitSummaryRow__title">{title}</div>
              <div className="eEventKitSummaryRow__meta">Loading kit details</div>
            </div>
          </div>
        );
      }

      return (
        <div className="eEventKitSummaryRow eEventKitSummaryRow--warning" key={key}>
          <div className="eEventKitSummaryRow__preview eEventKitSummaryRow__preview--empty">
            <span>!</span>
          </div>
          <div className="eEventKitSummaryRow__info">
            <div className="eEventKitSummaryRow__title">{title}</div>
            <div className="eEventKitSummaryRow__emptyText">
              Kit is assigned for this {subjectText}, but it could not be found.
            </div>
          </div>
          <div className="eEventKitSummaryRow__actions">
            <Button
              text="Change kit"
              onClick={() => openKitModalForTeam(teamId)}
              customClass="sit-btn sit-btn--secondary sit-btn--sm"
            />
            <Button
              text="Remove kit"
              onClick={() => handleClearKitForTeam(teamId)}
              customClass="sit-btn sit-btn--danger sit-btn--sm"
            />
          </div>
        </div>
      );
    }

    const layout = getLayoutForKit(kit as KitLabBackendKit);

    const kitGenderRaw = (kit!.gender || '').toString().toUpperCase();
    const genderText = kitGenderRaw === 'MALE' ? 'Boys' : kitGenderRaw === 'FEMALE' ? 'Girls' : 'Any team';

    const hasSports = Array.isArray(kit!.sportIds) && kit!.sportIds.length > 0;
    const sportsText = hasSports ? 'Sport-specific' : 'All sports';

    const pocketColor = resolveDetailColor(
      layout.pocketColorSource,
      layout.pocketCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const sleevesColor = resolveDetailColor(
      layout.sleevesColorSource,
      layout.sleevesCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const collarColor = resolveDetailColor(
      layout.collarColorSource,
      layout.collarCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const shoulderTrianglesColor = resolveDetailColor(
      layout.shoulderTrianglesColorSource,
      layout.shoulderTrianglesCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const sleeveCuffsColor = resolveDetailColor(
      layout.sleeveCuffsColorSource,
      layout.sleeveCuffsCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );

    const isSkort = isSkortPatternId(layout.bottomPattern);

    return (
      <div className="eEventKitSummaryRow" key={key}>
        <div className="eEventKitSummaryRow__preview">
          <KitShirtPreview
            variant={layout.shirtPattern}
            primaryColor={layout.primaryColor}
            secondaryColor={layout.secondaryColor}
            showPocket={layout.showPocket}
            showCollar={layout.showCollar}
            showSleeves={layout.showSleeves}
            showShoulderTriangles={layout.showShoulderTriangles}
            showSleeveCuffs={layout.showSleeveCuffs}
            pocketColor={pocketColor}
            sleevesColor={sleevesColor}
            collarColor={collarColor}
            shoulderTrianglesColor={shoulderTrianglesColor}
            sleeveCuffsColor={sleeveCuffsColor}
            stripeCount={layout.stripeCount}
            stripesEqualWidth={layout.stripesEqualWidth}
            stripePattern={layout.stripePattern}
            diagonalDirection={layout.diagonalDirection}
            chevronSize={layout.chevronSize}
            chevronDirection={layout.chevronDirection}
            chevronPosition={layout.chevronPosition}
            sideTrianglesMode={layout.sideTrianglesMode}
            sleevePattern={layout.sleevePattern}
            collarStyle={layout.collarStyle}
            className="eKitLab__shirtSvg--small"
          />

          {isSkort ? (
            <KitSkortPreview
              pattern={mapBottomToSkortPattern(layout.bottomPattern)}
              primaryColor={layout.shortsPrimaryColor}
              secondaryColor={layout.shortsSecondaryColor}
              stripeCount={layout.skortStripeCount}
              stripesEqualWidth={layout.skortStripesEqualWidth}
              stripePattern={layout.skortStripePattern}
              sideTrianglesMode={layout.skortSideTrianglesMode}
              className="eKitLab__bottomSvg--small eKitLab__bottomSvg--smallSkort"
            />
          ) : (
            <KitShortsPreview
              variant={
                layout.bottomPattern === 'SHORTS_SIDE'
                  ? 'sideStripes'
                  : layout.bottomPattern === 'SHORTS_TRIANGLES'
                  ? 'sideTriangles'
                  : layout.bottomPattern === 'SHORTS_BIG_TRIANGLES'
                  ? 'sideBigTriangles'
                  : layout.bottomPattern === 'SHORTS_SEMICIRCLES'
                  ? 'sideSemiCircles'
                  : 'plain'
              }
              primaryColor={layout.shortsPrimaryColor}
              secondaryColor={layout.shortsSecondaryColor}
              sideTrianglesPosition={layout.shortsTrianglesMode}
              className="eKitLab__bottomSvg--small"
            />
          )}
        </div>

        <div className="eEventKitSummaryRow__info">
          <div className="eEventKitSummaryRow__title">{title}</div>
          <div className="eEventKitSummaryRow__kitName">{kit!.name || 'Unnamed kit'}</div>
          <div className="eEventKitSummaryRow__meta">
            <span>{genderText}</span>
            <span>{sportsText}</span>
          </div>
        </div>

        <div className="eEventKitSummaryRow__actions">
          <Button
            text="Change kit"
            onClick={() => openKitModalForTeam(teamId)}
            customClass="sit-btn sit-btn--secondary sit-btn--sm"
          />
          <Button
            text="Remove kit"
            onClick={() => handleClearKitForTeam(teamId)}
            customClass="sit-btn sit-btn--danger sit-btn--sm"
          />
        </div>
      </div>
    );
  };

  const sectionModels: Array<{ teamId: string | null; title: string }> = [];

  if (ownTeams.length > 0) {
    ownTeams.forEach(team => {
      const teamId = team._id ? team._id.toString() : null;
      const title = `Kit for team ${team.name}`;
      sectionModels.push({ teamId, title });
    });
  } else {
    sectionModels.push({ teamId: null, title: 'Kit for this event' });
  }

  const assignedCount = sectionModels.reduce((count, section) => {
    const mapping = findMappingForTeam(section.teamId);
    return mapping && mapping.kitId ? count + 1 : count;
  }, 0);

  const totalCount = sectionModels.length;
  const missingCount = Math.max(totalCount - assignedCount, 0);

  return (
    <div className="eEventKitSummary">
      <div className="eEventKitSummaryHeader">
        <div>
          <h3 className="eEventKitSummaryHeader__title">Kit</h3>
          <div className="eEventKitSummaryHeader__subtitle">
            Assign the correct Kit Lab strip for this event{ownTeams.length > 0 ? ' and its teams' : ''}.
          </div>
        </div>
      </div>

      <div className="eEventKitSummaryStats">
        <div className="eEventKitSummaryStat eEventKitSummaryStat--teams">
          <span className="eEventKitSummaryStat__dot" />
          <span className="eEventKitSummaryStat__label">{ownTeams.length > 0 ? 'Teams' : 'Sections'}</span>
          <span className="eEventKitSummaryStat__value">{totalCount}</span>
        </div>
        <div className="eEventKitSummaryStat eEventKitSummaryStat--assigned">
          <span className="eEventKitSummaryStat__dot" />
          <span className="eEventKitSummaryStat__label">Assigned</span>
          <span className="eEventKitSummaryStat__value">{assignedCount}</span>
        </div>
        <div className="eEventKitSummaryStat eEventKitSummaryStat--missing">
          <span className="eEventKitSummaryStat__dot" />
          <span className="eEventKitSummaryStat__label">Missing</span>
          <span className="eEventKitSummaryStat__value">{missingCount}</span>
        </div>
      </div>

      <div className="eEventKitSummaryList">
        {sectionModels.map(section => renderSection(section.teamId, section.title))}
      </div>
      {renderKitModal()}
    </div>
  );
};

SchoolEventKitSummary.displayName = 'SchoolEventKitSummary';
